/**
 * Entitlement types + fetch/parse helpers shared by the billing context,
 * page, and gate components (plan §8). The readiness endpoint returns
 * a superset shape — here we only extract the billing-relevant fields.
 *
 * Contract mirrors ``excel_service/api/routers/settings.py`` —
 * `_entitlement_section()`. When TARIFF_GATING_ENABLED is off the
 * backend returns a permissive all-modules envelope, so every field
 * except `tariffGatingEnabled` is safe to treat as "unlimited".
 */

export type ModuleKey = 'base' | 'supply' | 'ai' | 'spp';

export type BillingState =
  | 'PENDING_FIRST_PAYMENT'
  | 'ACTIVE'
  | 'PAST_DUE'
  | 'READ_ONLY'
  | 'DOWNGRADED'
  | 'FROZEN'
  | 'CANCELLED'
  | null;

export interface Entitlement {
  tariffCode: string | null;
  enabledModules: ModuleKey[];
  skuQuota: number | null;
  skuQuotaUsed: number | null;
  billingState: BillingState;
  isDemo: boolean;
  tariffGatingEnabled: boolean;
}

const ALL_MODULES: ModuleKey[] = ['base', 'supply', 'ai', 'spp'];

/**
 * Permissive fallback used when:
 *   - readiness fetch fails (network down, 5xx)
 *   - readiness returns without an `entitlement` section (older backend)
 *   - the context is used before any fetch has completed
 *
 * We deliberately fail-open here to match the backend's stance under
 * `TARIFF_GATING_ENABLED=off`: Phase 1–5 must stay observably inert.
 */
export const PERMISSIVE_ENTITLEMENT: Entitlement = {
  tariffCode: null,
  enabledModules: [...ALL_MODULES],
  skuQuota: null,
  skuQuotaUsed: null,
  billingState: null,
  isDemo: false,
  tariffGatingEnabled: false,
};

function parseModuleList(raw: unknown): ModuleKey[] {
  if (!Array.isArray(raw)) return [...ALL_MODULES];
  const valid = new Set<ModuleKey>(ALL_MODULES);
  const result: ModuleKey[] = [];
  for (const item of raw) {
    if (typeof item === 'string' && valid.has(item as ModuleKey)) {
      result.push(item as ModuleKey);
    }
  }
  return result.length > 0 ? result : [...ALL_MODULES];
}

function parseBillingState(raw: unknown): BillingState {
  if (typeof raw !== 'string') return null;
  switch (raw) {
    case 'PENDING_FIRST_PAYMENT':
    case 'ACTIVE':
    case 'PAST_DUE':
    case 'READ_ONLY':
    case 'DOWNGRADED':
    case 'FROZEN':
    case 'CANCELLED':
      return raw;
    default:
      return null;
  }
}

/**
 * Extract the entitlement envelope from a readiness-endpoint payload.
 * Missing / malformed fields fall through to the permissive default —
 * the frontend must never lock a user out because of a bad payload.
 */
export function parseEntitlementFromReadiness(payload: unknown): Entitlement {
  if (!payload || typeof payload !== 'object') return PERMISSIVE_ENTITLEMENT;
  const section = (payload as Record<string, unknown>).entitlement;
  if (!section || typeof section !== 'object') return PERMISSIVE_ENTITLEMENT;
  const record = section as Record<string, unknown>;
  const skuQuota =
    typeof record.sku_quota === 'number' && record.sku_quota >= 0
      ? record.sku_quota
      : null;
  const skuQuotaUsed =
    typeof record.sku_quota_used === 'number' && record.sku_quota_used >= 0
      ? record.sku_quota_used
      : null;
  return {
    tariffCode:
      typeof record.tariff_code === 'string' && record.tariff_code !== ''
        ? record.tariff_code
        : null,
    enabledModules: parseModuleList(record.enabled_modules),
    skuQuota,
    skuQuotaUsed,
    billingState: parseBillingState(record.billing_state),
    isDemo: Boolean(record.is_demo),
    tariffGatingEnabled: Boolean(record.tariff_gating_enabled),
  };
}

/**
 * Whether a module should be treated as enabled in the UI. Demo shops
 * always pass (D12); if gating is off we also always pass (the backend
 * already confirmed this, but we double-check so a stale cache can't
 * produce a false-negative gate). Billing states READ_ONLY /
 * DOWNGRADED / CANCELLED disable paid modules but keep `base` alive —
 * matches the shop-side `has_module()` logic in `entitlement/cache.py`.
 */
export function isModuleEnabled(
  entitlement: Entitlement,
  module: ModuleKey,
): boolean {
  if (entitlement.isDemo) return true;
  if (!entitlement.tariffGatingEnabled) return true;
  if (
    module !== 'base' &&
    (entitlement.billingState === 'READ_ONLY' ||
      entitlement.billingState === 'DOWNGRADED' ||
      entitlement.billingState === 'CANCELLED')
  ) {
    return false;
  }
  return entitlement.enabledModules.includes(module);
}

/**
 * Convenience: true when the billing state indicates the user should
 * see a warning / action-required banner, regardless of module.
 */
export function hasBillingBanner(
  entitlement: Entitlement,
): 'past_due' | 'read_only' | 'downgraded' | 'frozen' | 'pending' | null {
  switch (entitlement.billingState) {
    case 'PAST_DUE':
      return 'past_due';
    case 'READ_ONLY':
      return 'read_only';
    case 'DOWNGRADED':
      return 'downgraded';
    case 'FROZEN':
      return 'frozen';
    case 'PENDING_FIRST_PAYMENT':
      return 'pending';
    default:
      return null;
  }
}
